import { TOKENS } from "./tokens";

/**
 * O fragmento de `theme.extend` que os três frontes compartilham.
 *
 * ## Por que existe
 *
 * Em 2026-09-09 os quatro `tailwind.config` do repo discordavam: `text-xs` era
 * 10,8px no web, 11px no mobile e 12px no desktop; `rounded-sm` era 4px em dois
 * e 2px no desktop; a família era Roboto no web e no mobile e a fonte do sistema
 * no desktop. O desktop era o desalinhado em tudo por um motivo estrutural — ele
 * não sobrescrevia nada, herdando o Tailwind cru enquanto os outros herdavam a
 * nossa versão.
 *
 * ## Por que arquivo próprio, e não dentro de `tokens.ts`
 *
 * O `tokens.ts` inteiro é construído sobre o eixo claro/escuro:
 * `Token = keyof TOKENS.light`, `hsl(token, scheme)`. Uma escala tipográfica
 * não tem esquema — 14px é 14px nos dois temas. Enfiá-la lá a deixaria solta no
 * meio de um arquivo cuja estrutura toda é `TOKENS[scheme][token]`, ou a
 * colocaria no tipo `Token`, que os três apps importam, prometendo um par
 * claro/escuro que não existe. Mesma razão do `motion.ts`.
 *
 * ## Por que duas formas
 *
 * O navegador quer `rem` e string; o React Native quer número puro. É a mesma
 * escala em dois formatos, e o teste é quem garante que continuam iguais.
 */

/** A escala, em pixel. É a fonte das duas formas — mude aqui. */
export const SCALE_PX = {
  "2xs": 8,
  xs: 10.8,
  /**
   * Tipografia de campo digitável. No nativo é a regra; no web vale só no
   * produto estreito (`[.app-scale_&]:max-lg:text-field`, TAM-262), onde o
   * web copia o app — no web largo e no desktop o campo segue em `text-sm`.
   *
   * 16px e **sem entrelinha**, por dois motivos de plataforma:
   *
   * 1. As classes `text-*` do Tailwind definem tamanho **e** `line-height`. Num
   *    `TextInput` do iOS, `lineHeight` maior que a fonte empurra o glifo para a
   *    base da caixa de linha enquanto o cursor é desenhado pela caixa inteira —
   *    o texto fica visivelmente mais baixo que o cursor.
   * 2. 16px, e não os 14 do web, porque o motivo é de toque e digitação, não de
   *    estilo — a mesma natureza da regra dos 44pt de alvo mínimo.
   *
   * Em `<Text>` use `text-base`; lá a entrelinha é necessária e está certa.
   */
  field: 16,
} as const;

const RADIUS_PX = { sm: 4, md: 6, lg: 8, xl: 12 } as const;

/**
 * Breakpoints do `theme.screens`. Fonte: `apps/web/tailwind.config.ts` antes
 * deste módulo — o default do Tailwind (`sm`/`md`/`lg`/`xl`/`2xl`) mais
 * `2xs`/`xs`/`3xl`, com `sm` sobrescrito para 540px no lugar dos 640px
 * default.
 *
 * O web não muda um pixel com a migração: os valores são os mesmos que já
 * calculava. Quem mudou foi o desktop: antes da Tarefa 16 ele não declarava
 * `screens` e herdava o default cru do Tailwind (`sm: 640px`) — agora quebra
 * no mesmo `sm: 540px` do web (paridade).
 */
export const SCREENS = {
  "2xs": "375px",
  xs: "390px",
  sm: "540px",
  md: "768px",
  lg: "1024px",
  xl: "1280px",
  "2xl": "1536px",
  "3xl": "1720px",
} as const;

/**
 * `10.8` → `"10.8px"`. Em px, e não em rem, de propósito (TAM-262): no web
 * estreito a raiz cai para 14px (`html.app-scale`, em `globals.css`), a
 * mesma conta do NativeWind (`inlineRem = 14`), e o que o app desenha em px
 * fixo — estes tokens — tem de continuar em px fixo no web. Com a raiz de 16px
 * (web largo, desktop) px e rem dão o mesmo tamanho.
 */
function px(value: number): string {
  return `${value}px`;
}

/**
 * Pilha do web. O primeiro item é a variável que o `next/font` preenche; o resto
 * é fallback.
 *
 * **Ressalva conhecida:** o desktop herda esta pilha mas não tem `next/font`,
 * então `--font-roboto-flex` fica indefinida lá. Um `var()` indefinido **sem**
 * fallback invalida a declaração `font-family` inteira em tempo de valor
 * computado — o CSS não cai para o próximo item da lista, e o resultado é a
 * fonte padrão do navegador. Medido no desktop em 2026-09-09: o computado saía
 * "Times New Roman". Por isso o fallback vai **dentro** do próprio `var()`:
 * com a variável definida (web) o resultado não muda; sem ela (desktop) resolve
 * para `ui-sans-serif`. Carregar a fonte no desktop é item próprio, fora deste
 * desenho.
 */
const SANS_STACK = [
  "var(--font-roboto-flex, ui-sans-serif)",
  "ui-sans-serif",
  "-apple-system",
  "BlinkMacSystemFont",
  "Segoe UI Variable Display",
  "Segoe UI",
  "Helvetica",
  "Apple Color Emoji",
  "Arial",
  "sans-serif",
  "Segoe UI Emoji",
  "Segoe UI Symbol",
  "Noto Color Emoji",
];

/**
 * O mapa `colors` do Tailwind, com **todos** os tokens de cor.
 *
 * Existe porque escrever essa lista à mão faz o app virar subconjunto sem
 * ninguém ver. Medido em 2026-09-09: o `apps/desktop` declarava 15 dos 32
 * tokens, e `bg-popover` — usado pelo `Select` que acabara de subir para o
 * `@tami/ui` — simplesmente não existia lá. A classe some, o fundo fica
 * transparente, e o menu aparece como texto solto sobre a tela. Sem erro.
 *
 * O `<alpha-value>` é o marcador que o Tailwind troca pela opacidade da classe
 * (`bg-popover/50`), e por isso o valor guardado no token é o trio HSL sem a
 * função em volta.
 */
export function tailwindColors(): Record<string, string> {
  const map: Record<string, string> = {};
  for (const name of Object.keys(TOKENS.light)) {
    map[name] = `hsl(var(--${name}) / <alpha-value>)`;
  }
  return map;
}

/**
 * As chaves são declaradas uma a uma, e não como `Record<string, …>`, por causa
 * do `noUncheckedIndexedAccess: true` do `tsconfig.base.json`: com assinatura de
 * índice, **todo** acesso — inclusive por ponto, `tema.fontSize.xs` — devolve
 * `string | undefined`, e quem consome tem de tratar um `undefined` que nunca
 * acontece. Chave declarada não passa por essa regra.
 *
 * O array é mutável de propósito: `readonly string[]` não satisfaz o tipo de
 * `fontFamily` do Tailwind, e os configs do web e do desktop usam
 * `satisfies Config`.
 *
 * O config do **mobile** não usa — ele não é um config de Tailwind puro, e o
 * tipo oficial exige string onde o `nativeTheme()` devolve número. O porquê está
 * escrito em `apps/mobile/tailwind.config.ts`.
 */
export interface WebTheme {
  readonly fontSize: { readonly "2xs": string; readonly xs: string; readonly field: string };
  readonly fontFamily: { readonly sans: string[] };
  readonly borderRadius: {
    readonly sm: string;
    readonly md: string;
    readonly lg: string;
    readonly xl: string;
  };
  readonly screens: typeof SCREENS;
}

export interface NativeTheme {
  readonly fontSize: {
    readonly "2xs": number;
    readonly xs: number;
    readonly field: number;
  };
  readonly fontFamily: {
    readonly sans: string[];
    readonly normal: string[];
    readonly medium: string[];
    readonly semibold: string[];
    readonly bold: string[];
  };
  readonly borderRadius: {
    readonly sm: number;
    readonly md: number;
    readonly lg: number;
    readonly xl: number;
  };
}

/**
 * Para `apps/web` e `apps/desktop`.
 *
 * **`fontSize` é string pura, sem entrelinha, de propósito.** A outra forma que
 * o Tailwind aceita — `["1rem", { lineHeight }]` — mudaria a entrelinha de 494
 * usos de `text-xs` no web, que está em produção.
 *
 * **`borderRadius` sai em valor literal, não em `calc(var(--radius) - Npx)`.**
 * O web derivava os três dessa variável e o desktop não a declara: herdar a
 * expressão colapsaria o raio dele. A variável continua existindo no
 * `globals.css` do web para quem a use direto.
 *
 * **`screens` é o breakpoint efetivo do web hoje, `sm` incluso.** Antes da
 * Tarefa 16, o desktop não declarava `screens` e herdava o default cru do
 * Tailwind (`sm: 640px`); ao adotar `webTheme().screens` ele passou a quebrar
 * no mesmo `sm: 540px` do web — a única mudança de comportamento visível
 * autorizada naquele lote.
 */
export function webTheme(): WebTheme {
  return {
    fontSize: {
      "2xs": px(SCALE_PX["2xs"]),
      xs: px(SCALE_PX.xs),
      field: px(SCALE_PX.field),
    },
    // Cópia, não a referência: `fontFamily.sans` é `string[]` mutável (exigência
    // do Tailwind), e web e desktop chamam `webTheme()` cada um pro seu config —
    // sem cópia, os dois compartilhariam a mesma instância do array.
    fontFamily: { sans: [...SANS_STACK] },
    borderRadius: {
      sm: px(RADIUS_PX.sm),
      md: px(RADIUS_PX.md),
      lg: px(RADIUS_PX.lg),
      xl: px(RADIUS_PX.xl),
    },
    screens: SCREENS,
  };
}

/**
 * Para `apps/mobile`.
 *
 * **A família carrega o peso.** O config do mobile desliga o core plugin
 * `fontWeight` porque no Android `fontWeight` não seleciona arquivo de fonte: o
 * peso precisa ser uma família. Sem isto, `.font-semibold` existiria duas vezes
 * — uma como peso, outra como família.
 */
export function nativeTheme(): NativeTheme {
  return {
    fontSize: {
      "2xs": SCALE_PX["2xs"],
      xs: SCALE_PX.xs,
      field: SCALE_PX.field,
    },
    fontFamily: {
      sans: ["Roboto_400Regular"],
      normal: ["Roboto_400Regular"],
      medium: ["Roboto_500Medium"],
      semibold: ["Roboto_600SemiBold"],
      bold: ["Roboto_700Bold"],
    },
    borderRadius: { ...RADIUS_PX },
  };
}
